import { forwardRef, type LabelHTMLAttributes } from 'react';
import { cn } from '../lib/utils.js';
import { RequiredMark } from './RequiredMark.js';

export interface LabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  /**
   * Append the required marker after the label text.
   *
   * This is the VISIBLE half only. The machine-readable half lives on the
   * control — `required` (or `aria-required`, which `<Input>`, `<Textarea>`,
   * `<Select>` and `<Combobox>` now derive from it). Set both: a marker with no
   * `required` is a promise the form does not keep, and a `required` with no
   * marker is the state 41 of this app's 48 required fields shipped in.
   */
  required?: boolean;
}

export const Label = forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, required, children, ...props }, ref) => (
    <label
      ref={ref}
      className={cn(
        // `text-label` is 12px/600 — the sister app's field-label step. It
        // carries its own weight and line-height, so no `font-*`/`leading-*`.
        //
        // `peer-disabled:cursor-not-allowed peer-disabled:opacity-70` used to
        // be here and was dead twice over. `peer-*` only reaches a SIBLING THAT
        // FOLLOWS the peer, and every label in this app precedes its input;
        // `peer` itself appears zero times in apps/web, so there was never a
        // peer to read. And 70% contradicted the one disabled value the system
        // has (`disabledFade`, 50% — a field faded to 70% looked more available
        // than the disabled button beside it, which is the exact bug focus.ts
        // was written to end). Deleted rather than wired: a disabled field
        // already fades itself, and fading its label too would compound to 35%,
        // which is the Checkbox bug this same pass fixed.
        'text-label text-foreground',
        className
      )}
      {...props}
    >
      {children}
      {required && <RequiredMark />}
    </label>
  )
);
Label.displayName = 'Label';
